import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  RecommendationEngineFilterConfig,
  RecommendationEngineFilterKind,
  SignalMetadata,
} from "../../../../types/recommendationEngine";
import {
  FILTER_HELP,
  FILTER_LABELS,
  SIGNAL_GROUP_DESCRIPTIONS,
} from "../copy";
import InfoTooltip from "./InfoTooltip";
import SignalDetailMenuItem from "./SignalDetailMenuItem";
import { engineTokens } from "../theme";

type Props = {
  filters: RecommendationEngineFilterConfig[];
  signals: SignalMetadata[];
  clamp01: boolean;
  onChange: (next: RecommendationEngineFilterConfig[]) => void;
};

const FILTER_KIND_ORDER: RecommendationEngineFilterKind[] = [
  "require_on_shopping_list",
  "exclude_source_merchant",
  "require_deals_page_eligible",
  "min_signal",
  "min_discount_percent",
  "max_flyer_list_age_days",
  "suppress_recently_served",
  "exclude_flyer_item_ids",
];

const createFilter = (
  kind: RecommendationEngineFilterKind,
  defaultSignalId: string,
): RecommendationEngineFilterConfig => {
  switch (kind) {
    case "min_signal":
      return { kind: "min_signal", signalId: defaultSignalId, min: 1 };
    case "require_on_shopping_list":
      return { kind: "require_on_shopping_list" };
    case "exclude_source_merchant":
      return { kind: "exclude_source_merchant" };
    case "min_discount_percent":
      return { kind: "min_discount_percent", minPercent: 10 };
    case "max_flyer_list_age_days":
      return { kind: "max_flyer_list_age_days", maxAgeDays: 14 };
    case "exclude_flyer_item_ids":
      return { kind: "exclude_flyer_item_ids", flyerItemIds: [] };
    case "require_deals_page_eligible":
      return { kind: "require_deals_page_eligible" };
    case "suppress_recently_served":
      return { kind: "suppress_recently_served", lookbackHours: 24 };
    default:
      return { kind: "require_deals_page_eligible" };
  }
};

const parseIdList = (value: string): string[] =>
  value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const FilterEditor = ({ filters, signals, clamp01, onChange }: Props) => {
  const [pendingKind, setPendingKind] =
    useState<RecommendationEngineFilterKind>("require_deals_page_eligible");

  const signalGroups = React.useMemo(() => {
    const groups: Record<string, SignalMetadata[]> = {};
    signals.forEach((s) => {
      if (!groups[s.group]) groups[s.group] = [];
      groups[s.group].push(s);
    });
    return groups;
  }, [signals]);

  const renderSignalMenuItems = () =>
    Object.entries(signalGroups).flatMap(([group, groupSignals]) => [
      <MenuItem
        key={`${group}-header`}
        disabled
        sx={{ opacity: 0.7, alignItems: "flex-start" }}
      >
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {group.toUpperCase()}
          </Typography>
          {SIGNAL_GROUP_DESCRIPTIONS[group] && (
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
              sx={{ whiteSpace: "normal" }}
            >
              {SIGNAL_GROUP_DESCRIPTIONS[group]}
            </Typography>
          )}
        </Box>
      </MenuItem>,
      ...groupSignals.map((s) => (
        <SignalDetailMenuItem
          key={s.id}
          signal={s}
          value={s.id}
          clamp01={clamp01}
        />
      )),
    ]);

  const updateFilter = (
    index: number,
    next: RecommendationEngineFilterConfig,
  ) => onChange(filters.map((f, i) => (i === index ? next : f)));

  const removeFilter = (index: number) =>
    onChange(filters.filter((_, i) => i !== index));

  const addFilter = () => {
    onChange([
      ...filters,
      createFilter(pendingKind, signals[0]?.id ?? ""),
    ]);
  };

  return (
    <Box>
      {filters.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.5 }}
        >
          No filters configured. All hydrated candidates will reach the
          formula.
        </Typography>
      )}

      {filters.map((filter, index) => (
        <Box
          key={`${filter.kind}-${index}`}
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
            border: `1px solid ${engineTokens.border}`,
            borderRadius: 1.5,
            p: 1.25,
            mb: 1,
            backgroundColor: engineTokens.bgPaper,
          }}
        >
          <Box sx={{ display: "inline-flex", alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {FILTER_LABELS[filter.kind]}
            </Typography>
            <InfoTooltip title={FILTER_HELP[filter.kind]} />
          </Box>

          {filter.kind === "min_signal" && (
            <>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Signal</InputLabel>
                <Select
                  value={filter.signalId}
                  label="Signal"
                  onChange={(e) =>
                    updateFilter(index, {
                      ...filter,
                      signalId: e.target.value,
                    })
                  }
                  renderValue={(selected) => {
                    const id = selected as string;
                    const match = signals.find((s) => s.id === id);
                    return match ? match.label : id;
                  }}
                >
                  {renderSignalMenuItems()}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label="Min value"
                value={filter.min}
                onChange={(e) =>
                  updateFilter(index, {
                    ...filter,
                    min: Number(e.target.value),
                  })
                }
                sx={{ width: 120 }}
              />
            </>
          )}

          {filter.kind === "min_discount_percent" && (
            <TextField
              size="small"
              type="number"
              label="Min discount %"
              value={filter.minPercent}
              onChange={(e) =>
                updateFilter(index, {
                  ...filter,
                  minPercent: Number(e.target.value),
                })
              }
              sx={{ width: 160 }}
            />
          )}

          {filter.kind === "max_flyer_list_age_days" && (
            <TextField
              size="small"
              type="number"
              label="Max age (days)"
              value={filter.maxAgeDays}
              onChange={(e) =>
                updateFilter(index, {
                  ...filter,
                  maxAgeDays: Number(e.target.value),
                })
              }
              sx={{ width: 160 }}
            />
          )}

          {filter.kind === "suppress_recently_served" && (
            <TextField
              size="small"
              type="number"
              label="Lookback (hours)"
              value={filter.lookbackHours}
              onChange={(e) =>
                updateFilter(index, {
                  ...filter,
                  lookbackHours: Number(e.target.value),
                })
              }
              sx={{ width: 170 }}
            />
          )}

          {filter.kind === "exclude_flyer_item_ids" && (
            <TextField
              size="small"
              fullWidth
              label="Flyer item IDs (comma-separated)"
              value={filter.flyerItemIds.join(", ")}
              onChange={(e) =>
                updateFilter(index, {
                  ...filter,
                  flyerItemIds: parseIdList(e.target.value),
                })
              }
              sx={{ minWidth: 320, flex: 1 }}
            />
          )}

          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            aria-label={`Remove ${FILTER_LABELS[filter.kind]} filter`}
            onClick={() => removeFilter(index)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          mt: 1.5,
          flexWrap: "wrap",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Add filter</InputLabel>
          <Select
            label="Add filter"
            value={pendingKind}
            onChange={(e) =>
              setPendingKind(
                e.target.value as RecommendationEngineFilterKind,
              )
            }
          >
            {FILTER_KIND_ORDER.map((kind) => (
              <MenuItem key={kind} value={kind}>
                <Box>
                  <Typography variant="body2">{FILTER_LABELS[kind]}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "normal" }}
                  >
                    {FILTER_HELP[kind]}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={addFilter}
        >
          Add
        </Button>
      </Box>
    </Box>
  );
};

export default FilterEditor;
